import { Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { currencyFormatter } from '~utilities/formatters';
import { IDictionary } from '~utilities/IDictionary';
import { IGlobalState } from '~reducer';
import { loanGetAction, loanInvestmentsListAction } from '~Loans/actions';
import { loanInvestmentsSelector, loanSelector } from '~Loans/selectors';
import dayjs from 'dayjs';
import IInvestment from '~Api/Investment/IInvestment';
import ILoan from '~Api/Loan/ILoan';
import Layout from './Layout';
import WorkflowStatusEnum from '~Api/Investment/WorkflowStatusEnum';

const workflowStatusLabels: IDictionary<string> = {
    [WorkflowStatusEnum.Cancelled]: 'Cancelled',
    [WorkflowStatusEnum.Funded]: 'Funded',
    [WorkflowStatusEnum.GracePeriod]: 'Grace Period',
    [WorkflowStatusEnum.Incomplete]: 'Incomplete',
    [WorkflowStatusEnum.Live]: 'Live',
    [WorkflowStatusEnum.Ready]: 'Ready',
    [WorkflowStatusEnum.Repaid]: 'Repaid',
};

export default function Investments(): ReactElement {
    const { loanUuid } = useParams<{ loanUuid: string }>();

    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, loanUuid));
    const investments: IDictionary<IInvestment> = useSelector((state: IGlobalState) => loanInvestmentsSelector(state, loanUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect(() => {
        if (!investments) {
            dispatch(loanInvestmentsListAction(loanUuid));
        }
    }, [
        dispatch,
        investments,
        loanUuid,
    ]);

    if (!loan || !investments) {
        return (
            <Layout uuid={loanUuid} section='investments'>
                <Typography.Title level={2}>Investments</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const columns: ColumnsType<IInvestment> = [
        {
            render: (investment: IInvestment) => <Link to={`/investments/${investment.uuid}`}>{investment.code}</Link>,
            title: 'Code',
            width: '13%',
        },
        {
            dataIndex: 'name',
            render: (name: string) => name || '-',
            title: 'Name',
        },
        {
            dataIndex: 'amountTotal',
            render: (amountTotal: number) => currencyFormatter.format(amountTotal),
            title: 'Amount Total',
            width: '15%',
        },
        {
            dataIndex: 'amountRemaining',
            render: (amountRemaining: number) => currencyFormatter.format(amountRemaining),
            title: 'Amount Remaining',
            width: '15%',
        },
        {
            dataIndex: 'workflowStatus',
            render: (workflowStatus: WorkflowStatusEnum) => workflowStatusLabels[workflowStatus],
            title: 'Status',
            width: '13%',
        },
        {
            dataIndex: 'createdTime',
            defaultSortOrder: 'descend',
            render: (createdTime: string) => dayjs(createdTime).format('DD/MM/YYYY'),
            sorter: (a: IInvestment, b: IInvestment) => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
            title: 'Created',
            width: '15%',
        },
    ];

    return (
        <Layout uuid={loanUuid} section='investments'>
            <Typography.Title className='title' level={2}>Investments</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(investments)}
                pagination={false}
                rowKey='uuid'
                size='middle'
            />
        </Layout>
    );
}
